import type { UndefinedOnPartialDeep } from 'type-fest';

import type { DtoPickupPointOpeningHours } from '@repo/api-types/order-retrieval';
import { translate } from '@repo/phrase';
import { assetTypes } from '@repo/types/asset';

interface OpeningHours {
    dayOfWeek: string;
    span: string;
}

export const formatDaysNames = (openingHours: UndefinedOnPartialDeep<DtoPickupPointOpeningHours[]>) => {
    const daysMap: Record<string, string> = {
        mon: 'MONDAY',
        tue: 'TUESDAY',
        wed: 'WEDNESDAY',
        thu: 'THURSDAY',
        fri: 'FRIDAY',
        sat: 'SATURDAY',
        sun: 'SUNDAY',
    };

    const normalizedOpeningHours = openingHours.map((day) => {
        let normalizedDay = 'UNKNOWN_DAY';

        const foundDay = Object.entries(daysMap).find(([key]) => day.dayOfWeek?.toLowerCase().includes(key));
        if (foundDay) {
            normalizedDay = foundDay[1];
        }

        return {
            dayOfWeek: normalizedDay,
            span: day.span ?? 'UNKNOWN_TIME',
        };
    });

    return normalizedOpeningHours;
};

export const sortOpeningHoursArray = (openingHours: UndefinedOnPartialDeep<DtoPickupPointOpeningHours[]>) => {
    const dayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

    const sortedOpeningHoursArray = openingHours.sort((a, b) => {
        return dayOrder.indexOf(a.dayOfWeek?.toUpperCase() ?? '') - dayOrder.indexOf(b.dayOfWeek?.toUpperCase() ?? '');
    });

    return sortedOpeningHoursArray;
};

export const formatOpeningHours = (
    openingHours: UndefinedOnPartialDeep<DtoPickupPointOpeningHours[]>,
    assetType: string
): Record<string, string> => {
    const formattedResult: Record<string, string> = {};

    const normalizedOpeningHours = assetType === assetTypes.order ? openingHours : formatDaysNames(openingHours);

    const sortedOpeningHours = sortOpeningHoursArray(normalizedOpeningHours).filter(
        (day): day is OpeningHours => Boolean(day.dayOfWeek) && Boolean(day.span)
    );

    let previousSpan = '';
    let currentDays: string[] = [];

    const addFormattedResult = () => {
        const daysRange =
            currentDays.length > 1 ? `${currentDays[0]} - ${currentDays[currentDays.length - 1]}` : currentDays[0];
        if (daysRange) {
            formattedResult[daysRange] = previousSpan;
        }
    };

    for (const day of sortedOpeningHours) {
        const formattedSpan =
            !day.span || day.span.toLowerCase().includes('closed')
                ? translate('closed')
                : day.span.replace(' ', '').replace('-', ' - ');

        if (formattedSpan !== previousSpan) {
            addFormattedResult();
            currentDays = [];
        }

        currentDays.push(translate(day.dayOfWeek.toLowerCase()));
        previousSpan = formattedSpan;
    }

    addFormattedResult();
    currentDays = [];
    return formattedResult;
};
